import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Progress } from '../ui/progress';
import { Badge } from '../ui/badge';
import {
  TrendingUp,
  Loader2,
  Target,
  CheckCircle2,
  AlertCircle,
  Sparkles,
  Clock,
  DollarSign,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { aiAPI } from '../../services/api';

const SuccessPredictor = ({ campaignId, compact = false }) => {
  const [loading, setLoading] = useState(false);
  const [prediction, setPrediction] = useState(null);
  const [expanded, setExpanded] = useState(false);

  const fetchPrediction = async () => {
    setLoading(true);
    try {
      const result = await aiAPI.predictSuccess(campaignId);
      setPrediction(result);
    } catch (err) {
      console.error('Prediction failed:', err);
    } finally {
      setLoading(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 75) return 'text-emerald-400';
    if (score >= 50) return 'text-emerald-400';
    if (score >= 25) return 'text-yellow-400';
    return 'text-red-400';
  };

  const getScoreGradient = (score) => {
    if (score >= 75) return 'from-emerald-500 to-emerald-500';
    if (score >= 50) return 'from-emerald-500 to-blue-500';
    if (score >= 25) return 'from-yellow-500 to-orange-500';
    return 'from-red-500 to-orange-500';
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  if (!prediction && !loading) {
    return (
      <div className="bg-gradient-to-r from-teal-500/10 to-emerald-500/10 rounded-xl p-4 border border-teal-500/30">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-teal-500" />
            <span className="text-foreground font-medium">AI Success Predictor</span>
          </div>
          <Button
            size="sm"
            onClick={fetchPrediction}
            className="bg-teal-500 hover:bg-teal-600"
          >
            <Sparkles className="w-4 h-4 mr-1" /> Predict
          </Button>
        </div>
        <p className="text-xs text-muted-foreground mt-2">Get AI-powered success probability analysis</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="bg-card rounded-xl p-4 border border-border">
        <div className="flex items-center justify-center gap-2 py-4">
          <Loader2 className="w-5 h-5 text-teal-500 animate-spin" />
          <span className="text-muted-foreground">Analyzing campaign...</span>
        </div>
      </div>
    );
  }

  if (compact) {
    return (
      <div className="flex items-center gap-2">
        <div className={`text-lg font-bold ${getScoreColor(prediction.success_probability)}`}>
          {prediction.success_probability}%
        </div>
        <span className="text-xs text-muted-foreground">success rate</span>
      </div>
    );
  }

  return (
    <div className="bg-card/50 rounded-xl border border-border overflow-hidden">
      {/* Header with Score */}
      <div className={`p-4 bg-gradient-to-r ${getScoreGradient(prediction.success_probability)} bg-opacity-20`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`text-4xl font-bold ${getScoreColor(prediction.success_probability)}`}>
              {prediction.success_probability}%
            </div>
            <div>
              <p className="text-foreground font-medium">Success Probability</p>
              <Badge className={`${
                prediction.confidence_level === 'high' ? 'bg-emerald-500/20 text-emerald-400' :
                prediction.confidence_level === 'medium' ? 'bg-emerald-500/20 text-emerald-400' :
                'bg-yellow-500/20 text-yellow-400'
              }`}>
                {prediction.confidence_level} confidence
              </Badge>
            </div>
          </div>
          <TrendingUp className={`w-8 h-8 ${getScoreColor(prediction.success_probability)}`} />
        </div>
        <Progress value={prediction.success_probability} className="mt-3 h-2" />
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 gap-4 p-4 border-b border-border">
        <div className="flex items-center gap-2">
          <DollarSign className="w-4 h-4 text-emerald-400" />
          <div>
            <p className="text-xs text-muted-foreground">Predicted Amount</p>
            <p className="text-foreground font-medium">{formatCurrency(prediction.predicted_amount)}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Clock className="w-4 h-4 text-emerald-400" />
          <div>
            <p className="text-xs text-muted-foreground">Timeline</p>
            <p className="text-foreground font-medium">{prediction.predicted_timeline}</p>
          </div>
        </div>
      </div>

      {/* Expandable Factors */}
      <div className="p-4">
        <button
          onClick={() => setExpanded(!expanded)}
          className="flex items-center justify-between w-full text-sm"
        >
          <span className="text-muted-foreground">View Analysis Factors</span>
          {expanded ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
        </button>

        {expanded && (
          <div className="mt-4 space-y-3">
            {/* Factors */}
            {prediction.factors?.map((factor, i) => (
              <div key={i} className="flex items-start gap-2 p-2 bg-background/50 rounded-lg">
                {factor.impact === 'positive' ? (
                  <CheckCircle2 className="w-4 h-4 text-emerald-400 mt-0.5" />
                ) : factor.impact === 'negative' ? (
                  <AlertCircle className="w-4 h-4 text-red-400 mt-0.5" />
                ) : (
                  <Target className="w-4 h-4 text-muted-foreground mt-0.5" />
                )}
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <span className="text-foreground text-sm font-medium">{factor.factor}</span>
                    <span className={`text-xs ${
                      factor.impact === 'positive' ? 'text-emerald-400' :
                      factor.impact === 'negative' ? 'text-red-400' : 'text-muted-foreground'
                    }`}>
                      {factor.score}/10
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground">{factor.reason}</p>
                </div>
              </div>
            ))}

            {/* Recommendations */}
            {prediction.recommendations?.length > 0 && (
              <div className="mt-4 p-3 bg-teal-500/10 rounded-lg border border-teal-500/30">
                <p className="text-sm font-medium text-teal-400 mb-2">💡 Recommendations</p>
                <ul className="space-y-1">
                  {prediction.recommendations.map((rec, i) => (
                    <li key={i} className="text-xs text-foreground/80 flex items-start gap-2">
                      <span className="text-teal-400">•</span>
                      {rec}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Refresh */}
      <div className="px-4 pb-4">
        <Button
          size="sm"
          variant="outline"
          onClick={fetchPrediction}
          className="w-full border-border"
        >
          <TrendingUp className="w-4 h-4 mr-2" /> Refresh Prediction
        </Button>
      </div>
    </div>
  );
};

export default SuccessPredictor;
